import type { JobSearchConstraints } from "@tabbio/types";
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import {
  DEFAULT_CV_CONFIG as SHARED_DEFAULT_CV_CONFIG,
  withCvFitToOnePage,
  withCvHideTabbioLink,
  withCvPhotoVisibility,
} from "@tabbio/utils/cv-config";
import { normalizeShareCardConfig } from "@tabbio/utils/cv-share-card";
import {
  DEFAULT_PROFILE_PRIVACY_SETTINGS,
  normalizeProfilePrivacySettings,
} from "@tabbio/utils/profile-privacy";
import type {
  ProfilePrivacySettings,
  ProfilePrivateInfo,
  ShareCardConfig,
  ExperienceLocationType,
  SmartCVPublicProject,
} from "@tabbio/types";
import type { SearchArea } from "./search-area-types";
import { captureSentryException } from "./sentry";
import type { CvSectionsConfig } from "./cv-section-config";

// ─── Types ──────────────────────────────────────────────────────────

export interface CVHighlight {
  id?: string;
  title: string;
  desc: string;
}

export interface CVSkillGroup {
  category: string;
  items: string[];
}

export interface CVExperience {
  id: string;
  role: string;
  company: string;
  location: string;
  locationType?: ExperienceLocationType | "";
  employmentType: string;
  period: string;
  overview: string;
  bullets: string[];
}

export interface CVEducation {
  id: string;
  /** Source start date; the form edits graduation year independently. */
  startDate?: string | null;
  degree: string;
  school: string;
  year: string;
  description: string;
}

export interface CVCertification {
  id: string;
  name: string;
  issuer: string;
  date: string;
  /** Optional verifiable credential link (e.g. Credly/AWS badge URL). */
  credentialUrl?: string;
}

export interface CVJobPreferences extends JobSearchConstraints {
  openToWork: boolean;
  jobTitles: string[];
  locationTypes: string[];
  preferredLocations: string[];
  employmentTypes: string[];
  /** Precise geo+radius areas (SCRUM-359). Prefer over `preferredLocations`
   * for distance-based ranking; `preferredLocations` is kept in sync by the
   * Job Preferences form for legacy readers (CV PDF, public profile). */
  searchAreas?: SearchArea[];
  /** Single-select relocation willingness (display label). */
  relocation?: string;
  /** "Where To?": relocation / work regions (multi-select display labels). */
  relocationAreas?: string[];
  /** Single-select notice period (display label). */
  noticePeriod?: string;
}

export interface CVProfile {
  name: string;
  username: string;
  headline: string;
  location: string;
  avatarUrl: string;
  contactEmail: string;
  phone: string;
  website: string;
  linkedin: string;
  github?: string;
  dribbble?: string;
  behance?: string;
  summary: string;
  highlights: CVHighlight[];
  skillGroups: CVSkillGroup[];
  /**
   * Client-only. Maps a skill name → the CV item ids (experience/education/
   * certification) the user said it's backed by, captured in the redesigned
   * onboarding skills step. Persisted only inside the local CV draft: it is
   * intentionally NOT mapped by `buildSaveSectionsFromCvProfile`, so it never
   * leaves the device (no backend field for attribution exists yet).
   */
  skillAttributions?: Record<string, string[]>;
  experience: CVExperience[];
  education: CVEducation[];
  projects?: SmartCVPublicProject[];
  certifications: CVCertification[];
  languages: string[];
  jobPreferences: CVJobPreferences;
  /**
   * Demographic / eligibility info from the "Help Recruiters Match You"
   * onboarding step. Persisted to `Profile.privateInfo` (employers-only), never
   * shown on the public CV.
   */
  privateInfo?: ProfilePrivateInfo;
}

// ─── CV Config ──────────────────────────────────────────────────────

export type CVLayoutStyle = "MODERN" | "CLASSIC" | "EXECUTIVE" | "CREATIVE";

export interface CVConfig {
  style: CVLayoutStyle;
  color: string;
  showPhoto: boolean;
  /**
   * Best-effort single-page mode (off by default): the PDF export shrinks its
   * print tokens until the CV fits one A4 page (bounded by a legibility
   * floor) and the web preview scales the sheet content the same way. Native
   * preview has no page metaphor, so it only persists the choice.
   */
  fitToOnePage?: boolean;
  /**
   * Hide the `{handle}.tabbio.com` row from this CV's contact header on every
   * surface (Pro: individual.custom_branding). Re-coerced server-side, so a
   * lapsed subscriber's CVs print the link again.
   */
  hideTabbioLink?: boolean;
  /** Per-CV share/OG card customization. Persisted as part of cvConfig. */
  card?: ShareCardConfig | null;
}

export const LAYOUT_OPTIONS: { id: CVLayoutStyle; label: string }[] = [
  { id: "MODERN", label: "Modern" },
  { id: "CLASSIC", label: "Classic" },
  { id: "EXECUTIVE", label: "Executive" },
  { id: "CREATIVE", label: "Creative" },
];

export const COLOR_OPTIONS = [
  { id: "PURPLE", hex: "#5A2AFF" },
  { id: "BLACK", hex: "#0A0A0A" },
  { id: "BLUE", hex: "#2563EB" },
  { id: "EMERALD", hex: "#059669" },
  { id: "RED", hex: "#C8102E" },
  { id: "ORANGE", hex: "#EA580C" },
];

const COLOR_MAP: Record<string, string> = Object.fromEntries(
  COLOR_OPTIONS.map((c) => [c.id, c.hex]),
);

export const DEFAULT_CV_CONFIG: CVConfig = {
  ...SHARED_DEFAULT_CV_CONFIG,
};

export function areCvConfigsEqual(a: CVConfig, b: CVConfig): boolean {
  const leftCard = normalizeShareCardConfig(a.card) ?? null;
  const rightCard = normalizeShareCardConfig(b.card) ?? null;
  return (
    a.style === b.style &&
    a.color === b.color &&
    a.showPhoto === b.showPhoto &&
    (a.fitToOnePage === true) === (b.fitToOnePage === true) &&
    (a.hideTabbioLink === true) === (b.hideTabbioLink === true) &&
    JSON.stringify(leftCard) === JSON.stringify(rightCard)
  );
}

// ─── Helpers ────────────────────────────────────────────────────────

export function getInitials(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export function getAccentColor(config: CVConfig): string {
  // BLACK is the canonical default accent; legacy raw-hex values pass through.
  return COLOR_MAP[config.color] || config.color || "#0A0A0A";
}

export function isColorDark(hex: string): boolean {
  const c = hex.replace("#", "");
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.65;
}

function parseHex(hex: string): [number, number, number] | null {
  let c = hex.replace("#", "").trim();
  // 3-digit shorthand (#000, #fff, #a1b) expands by doubling each nibble.
  // Without this a custom short-hex accent parsed to null, `contrastRatio`
  // returned its 21 fallback, and the readability guard silently no-op'd -
  // letting a near-black `#000` accent through as "fine" on a dark surface.
  if (/^[0-9a-fA-F]{3}$/.test(c)) {
    c = c
      .split("")
      .map((ch) => ch + ch)
      .join("");
  }
  if (c.length < 6) return null;
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  if ([r, g, b].some((v) => Number.isNaN(v))) return null;
  return [r, g, b];
}

/** WCAG relative luminance of an sRGB hex color (0 = black, 1 = white). */
function relativeLuminance(hex: string): number | null {
  const rgb = parseHex(hex);
  if (!rgb) return null;
  const [r, g, b] = rgb.map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two hex colors (1 = identical, 21 = black↔white). */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  if (la === null || lb === null) return 21;
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Accent colors are picked to sit on a white CV "paper" (the web renderer keeps
 * that paper white in every theme). The native preview, however, draws on the
 * app's themed background: near-black in dark mode: so a genuinely dark accent
 * (e.g. a near-black or deep-navy accent) used as heading/name text can drop to
 * invisible against the dark surface.
 *
 * This does NOT recolor every accent in dark mode: a purple, blue, green, etc.
 * still has ample contrast on black and keeps its branded color. Only when the
 * accent fails contrast against the *actual* background (dark-on-dark) do we
 * fall back to the theme's primary text color. `3:1` is the WCAG AA floor for
 * bold/large text, which these headings are.
 */
export function readableAccentText(
  accent: string,
  background: string,
  fallback: string,
): string {
  return contrastRatio(accent, background) >= 3 ? accent : fallback;
}

// ─── Mock Data ──────────────────────────────────────────────────────

export const MOCK_PROFILE: CVProfile = {
  name: "Ahmed Al Shamsi",
  username: "ahmedalshamsi",
  headline: "HR Manager | People Operations & Talent Strategy",
  location: "Abu Dhabi, UAE",
  avatarUrl: "",
  contactEmail: "hello@tabbio.com",
  phone: "+971 50 XXX XXXX",
  website: "ahmedalshamsi.tabbio.com",
  linkedin: "linkedin.com/in/ahmedaldhraif",
  github: "",
  dribbble: "",
  behance: "",
  summary:
    "Results-driven HR strategist with 10+ years of experience across government and private sectors in the UAE. Specializing in talent acquisition, employee engagement, and organizational development. Currently leading People Operations at Solution+ (Mubadala subsidiary) while building Tabbio: a platform transforming how professionals present themselves and how companies hire.",
  highlights: [
    { title: "182K LinkedIn Followers", desc: "#1 HR Voice in UAE" },
    { title: "20K+ Tabbio Users", desc: "In 3 months, zero paid ads" },
    { title: "120+ Companies", desc: "Onboarded organically" },
  ],
  skillGroups: [
    {
      category: "HR & People",
      items: [
        "HR Strategy",
        "Talent Acquisition",
        "Employee Relations",
        "UAE Labor Law",
        "Performance Management",
      ],
    },
    {
      category: "Leadership",
      items: [
        "Organizational Development",
        "Change Management",
        "Workforce Planning",
        "Succession Planning",
      ],
    },
    {
      category: "Operations",
      items: [
        "Compensation & Benefits",
        "HR Analytics",
        "HRIS",
        "Training & Development",
        "Employer Branding",
      ],
    },
  ],
  experience: [
    {
      id: "1",
      role: "HR Manager",
      company: "Solution+",
      location: "Abu Dhabi, UAE",
      locationType: "On-site",
      employmentType: "Full-time",
      period: "2021 - Present",
      overview:
        "Leading HR operations for Mubadala subsidiary. Managing full employee lifecycle, policy development, and organizational design.",
      bullets: [
        "Manage HR operations for 200+ employees across 3 entities",
        "Reduced time-to-hire by 40% through process automation",
        "Led Emiratization initiatives achieving 30% national workforce",
        "Implemented performance management system with OKR framework",
      ],
    },
    {
      id: "2",
      role: "Founder & CEO",
      company: "Tabbio",
      location: "Abu Dhabi, UAE",
      locationType: "Hybrid",
      employmentType: "Full-time",
      period: "2025 - Present",
      overview:
        "Building the future of professional profiles. Tabbio replaces static PDF CVs with smart, living link-based CVs.",
      bullets: [
        "Grew to 20,000+ users in 3 months with zero paid ads",
        "120+ companies onboarded organically",
        "240K+ content creator reach",
        "Built SmartCV analytics, UAE Pass verification, OAuth API",
      ],
    },
    {
      id: "3",
      role: "Senior HR Specialist",
      company: "Department of Government Enablement",
      location: "Abu Dhabi, UAE",
      locationType: "On-site",
      employmentType: "Full-time",
      period: "2018 - 2021",
      overview:
        "Oversaw recruitment, onboarding, and HR policy development for government entity.",
      bullets: [],
    },
  ],
  education: [
    {
      id: "1",
      degree: "Bachelor of Science",
      school: "Indiana State University",
      year: "2014",
      description: "Focus on Management and Organizational Behavior",
    },
  ],
  certifications: [
    { id: "1", name: "SHRM-CP", issuer: "SHRM", date: "Issued Jun 2020" },
    {
      id: "2",
      name: "Certified HR Professional",
      issuer: "CIPD",
      date: "Issued Mar 2019",
    },
  ],
  languages: ["Arabic (Native)", "English (Fluent)"],
  jobPreferences: {
    openToWork: true,
    jobTitles: ["HR Manager", "People Operations Lead", "Head of HR"],
    locationTypes: ["On-site", "Hybrid"],
    preferredLocations: ["Abu Dhabi", "Dubai"],
    employmentTypes: ["Full-time"],
  },
};

// ─── Context ────────────────────────────────────────────────────────

interface CVDataContextValue {
  profile: CVProfile;
  /** Ref that's synchronously updated inside setProfile: use in onSave callbacks */
  profileRef: React.MutableRefObject<CVProfile>;
  target?: string | null;
  /**
   * True when the user is viewing a delegated/tailored CV (an explicit route
   * target), as opposed to their own master CV. Derived from the ROUTE target,
   * NOT `effectiveTarget`: the master route can resolve `target` to an
   * auto-apply `defaultCvId` for display, but master-only actions ("Update
   * resume", snapshot "Revert") must still show. Screens gate those actions on
   * `!isDelegated`, never on `target`.
   */
  isDelegated: boolean;
  /**
   * True when the profile came from a single shared CV (CV-scoped delegated
   * grant). Screens use it to hide affordances with no tailored-CV equivalent
   * (job preferences, open-to-work, cover image).
   */
  isCvScoped: boolean;
  updateProfile: (updates: Partial<CVProfile>) => void;
  toggleOpenToWork: () => void;
  cvConfig: CVConfig;
  updateCVConfig: (updates: Partial<CVConfig>) => void;
  /** Change only CV-layout photo visibility. */
  setCVPhotoVisibility: (showPhoto: boolean) => void;
  /** Change only the independent one-page PDF preference. */
  setCVFitToOnePage: (enabled: boolean) => void;
  /** Change only the Pro "hide the Tabbio link" preference. */
  setCVHideTabbioLink: (hidden: boolean) => void;
  privacy: ProfilePrivacySettings;
  updatePrivacy: (privacy: ProfilePrivacySettings) => void;
  /**
   * Synchronously flush any pending debounced cvConfig save. Use after a
   * snapshot-revert call when the caller is about to unmount, to guarantee
   * the revert hits the backend before the previous edit's timer can fire
   * post-unmount (and to surface save errors instead of swallowing them in an
   * orphaned timer).
   */
  flushCVConfigSave: () => void;
  accentColor: string;
  /**
   * Skill-applied section include-list + order for the active CV (skills
   * two-way design, mirrors `TailoredCVOverrides.sectionsConfig`). Server
   * derived by layouts from the resolved CV / overrides; edit-section screens
   * may update it for CV-scoped contexts. null = legacy hardcoded section order.
   */
  sectionsConfig: CvSectionsConfig;
  updateSectionsConfig: (config: CvSectionsConfig) => void;
}

export const CVDataContext = createContext<CVDataContextValue | null>(null);

export const EMPTY_PROFILE: CVProfile = {
  name: "",
  username: "",
  headline: "",
  location: "",
  avatarUrl: "",
  contactEmail: "",
  phone: "",
  website: "",
  linkedin: "",
  github: "",
  dribbble: "",
  behance: "",
  summary: "",
  highlights: [],
  skillGroups: [],
  experience: [],
  projects: [],
  education: [],
  certifications: [],
  languages: [],
  jobPreferences: {
    openToWork: false,
    jobTitles: [],
    locationTypes: [],
    preferredLocations: [],
    employmentTypes: [],
  },
  privateInfo: {},
};

export const ONBOARDING_INITIAL_PROFILE: CVProfile = {
  ...EMPTY_PROFILE,
  jobPreferences: { ...EMPTY_PROFILE.jobPreferences, openToWork: true },
};

type LooseExperience = {
  title?: string;
  role?: string;
  current?: boolean;
  startDate?: string;
  endDate?: string;
};

export function getMostRecentExperience<T extends LooseExperience>(
  experiences: readonly T[] | undefined | null,
): T | null {
  if (!experiences?.length) return null;
  const current = experiences.find((e) => e?.current === true);
  if (current) return current;
  const rawDate = (e: T) => e?.endDate || e?.startDate || "";
  const score = (e: T) => {
    const parsed = Date.parse(rawDate(e));
    return Number.isFinite(parsed) ? parsed : 0;
  };
  return (
    [...experiences].sort((a, b) => {
      const diff = score(b) - score(a);
      if (diff !== 0) return diff;
      // Tiebreaker for unparseable dates ("Jan 2024" on Hermes, missing
      // dates): lexical compare so we don't silently fall back to
      // experiences[0]: the exact bug SCRUM-282 was filed to fix.
      return rawDate(b).localeCompare(rawDate(a));
    })[0] ?? null
  );
}

interface CVDataProviderProps {
  children: React.ReactNode;
  initialProfile?: CVProfile;
  initialCvConfig?: CVConfig;
  initialPrivacy?: ProfilePrivacySettings;
  /** Server-derived section include-list/order for the active CV. */
  sectionsConfig?: CvSectionsConfig;
  target?: string | null;
  /**
   * Whether this is a delegated/tailored CV view. Defaults to
   * `Boolean(target && target !== 'master')`, but the master CV layout passes
   * the ROUTE target explicitly so an auto-apply `defaultCvId` leaking into
   * `target` does not falsely mark the master CV as delegated.
   */
  isDelegated?: boolean;
  isCvScoped?: boolean;
  onCvConfigChange?: (config: CVConfig) => void;
  onSectionsConfigChange?: (config: CvSectionsConfig) => void;
  onPrivacyChange?: (privacy: ProfilePrivacySettings) => void;
  onOpenToWorkToggle?: (openToWork: boolean) => void;
}

export function CVDataProvider({
  children,
  initialProfile,
  initialCvConfig,
  initialPrivacy,
  sectionsConfig = null,
  target = null,
  isDelegated,
  isCvScoped = false,
  onCvConfigChange,
  onSectionsConfigChange,
  onPrivacyChange,
  onOpenToWorkToggle,
}: CVDataProviderProps) {
  const resolvedIsDelegated =
    isDelegated ?? Boolean(target && target !== "master");
  const [profile, setProfile] = useState<CVProfile>(
    initialProfile ?? EMPTY_PROFILE,
  );
  const [cvConfig, setCVConfig] = useState<CVConfig>(
    initialCvConfig ?? DEFAULT_CV_CONFIG,
  );
  const [activeSectionsConfig, setActiveSectionsConfig] =
    useState<CvSectionsConfig>(sectionsConfig ?? null);
  const [privacy, setPrivacy] = useState<ProfilePrivacySettings>(
    normalizeProfilePrivacySettings(
      initialPrivacy,
      DEFAULT_PROFILE_PRIVACY_SETTINGS,
    ),
  );
  const profileRef = useRef<CVProfile>(profile);
  // Keep the latest config outside React's render cycle so atomic actions and
  // an immediate flush always operate on the same snapshot.
  const cvConfigRef = useRef<CVConfig>(cvConfig);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync profile when initialProfile changes (e.g., React Query data arrives)
  const prevInitialRef = useRef(initialProfile);
  useEffect(() => {
    if (initialProfile && initialProfile !== prevInitialRef.current) {
      prevInitialRef.current = initialProfile;
      setProfile(initialProfile);
      profileRef.current = initialProfile;
    }
  }, [initialProfile]);

  // Sync cvConfig when initialCvConfig changes
  const prevCvConfigRef = useRef(initialCvConfig);
  useEffect(() => {
    if (initialCvConfig && initialCvConfig !== prevCvConfigRef.current) {
      prevCvConfigRef.current = initialCvConfig;
      setCVConfig(initialCvConfig);
    }
  }, [initialCvConfig]);

  const prevSectionsConfigRef = useRef(sectionsConfig);
  useEffect(() => {
    if (sectionsConfig !== prevSectionsConfigRef.current) {
      prevSectionsConfigRef.current = sectionsConfig;
      setActiveSectionsConfig(sectionsConfig ?? null);
    }
  }, [sectionsConfig]);

  const prevPrivacyRef = useRef(initialPrivacy);
  useEffect(() => {
    if (initialPrivacy && initialPrivacy !== prevPrivacyRef.current) {
      prevPrivacyRef.current = initialPrivacy;
      setPrivacy(
        normalizeProfilePrivacySettings(
          initialPrivacy,
          DEFAULT_PROFILE_PRIVACY_SETTINGS,
        ),
      );
    }
  }, [initialPrivacy]);

  const updateProfile = useCallback((updates: Partial<CVProfile>) => {
    const next = { ...profileRef.current, ...updates };
    profileRef.current = next;
    setProfile(next);
  }, []);

  const updatePrivacy = useCallback(
    (nextPrivacy: ProfilePrivacySettings) => {
      const normalized = normalizeProfilePrivacySettings(nextPrivacy, privacy);
      setPrivacy(normalized);
      if (onPrivacyChange) {
        onPrivacyChange(normalized);
      }
    },
    [onPrivacyChange, privacy],
  );

  const toggleOpenToWork = useCallback(() => {
    setProfile((prev) => {
      const newOpenToWork = !prev.jobPreferences.openToWork;
      const next = {
        ...prev,
        jobPreferences: {
          ...prev.jobPreferences,
          openToWork: newOpenToWork,
        },
      };
      profileRef.current = next;
      if (onOpenToWorkToggle) onOpenToWorkToggle(newOpenToWork);
      return next;
    });
  }, [onOpenToWorkToggle]);

  // Debounced save to backend on config change. cvConfigRef mirrors state so
  // flushCVConfigSave can read the latest value synchronously (state may not
  // have flushed by the time the caller asks to flush in the same tick).
  const safeInvokeOnChange = useCallback(
    (next: CVConfig) => {
      if (!onCvConfigChange) return;
      try {
        onCvConfigChange(next);
      } catch (err) {
        captureSentryException(err, { scope: "cv_config_save" });
      }
    },
    [onCvConfigChange],
  );

  const commitCVConfig = useCallback(
    (resolveNext: (current: CVConfig) => CVConfig) => {
      setCVConfig((prev) => {
        const next = resolveNext(prev);
        if (areCvConfigsEqual(prev, next)) {
          cvConfigRef.current = prev;
          return prev;
        }
        cvConfigRef.current = next;
        if (onCvConfigChange) {
          if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
          saveTimerRef.current = setTimeout(() => {
            saveTimerRef.current = null;
            safeInvokeOnChange(next);
          }, 300);
        }
        return next;
      });
    },
    [onCvConfigChange, safeInvokeOnChange],
  );

  const updateCVConfig = useCallback(
    (updates: Partial<CVConfig>) => {
      commitCVConfig((current) => ({ ...current, ...updates }));
    },
    [commitCVConfig],
  );

  const setCVPhotoVisibility = useCallback(
    (showPhoto: boolean) => {
      commitCVConfig((current) => withCvPhotoVisibility(current, showPhoto));
    },
    [commitCVConfig],
  );

  const setCVFitToOnePage = useCallback(
    (enabled: boolean) => {
      commitCVConfig((current) => withCvFitToOnePage(current, enabled));
    },
    [commitCVConfig],
  );

  const setCVHideTabbioLink = useCallback(
    (hidden: boolean) => {
      commitCVConfig((current) => withCvHideTabbioLink(current, hidden));
    },
    [commitCVConfig],
  );

  const updateSectionsConfig = useCallback(
    (config: CvSectionsConfig) => {
      setActiveSectionsConfig(config);
      if (onSectionsConfigChange) {
        onSectionsConfigChange(config);
      }
    },
    [onSectionsConfigChange],
  );

  const flushCVConfigSave = useCallback(() => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
      safeInvokeOnChange(cvConfigRef.current);
    }
  }, [safeInvokeOnChange]);

  // Prevent a pending timer from firing after the provider unmounts (would
  // leak the closure and silently fail any backend error).
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
    };
  }, []);

  const accentColor = getAccentColor(cvConfig);

  return React.createElement(
    CVDataContext.Provider,
    {
      value: {
        profile,
        profileRef,
        updateProfile,
        toggleOpenToWork,
        cvConfig,
        updateCVConfig,
        setCVPhotoVisibility,
        setCVFitToOnePage,
        setCVHideTabbioLink,
        privacy,
        updatePrivacy,
        flushCVConfigSave,
        accentColor,
        sectionsConfig: activeSectionsConfig,
        updateSectionsConfig,
        target,
        isDelegated: resolvedIsDelegated,
        isCvScoped,
      },
    },
    children,
  );
}

export function useCVData(): CVDataContextValue {
  const ctx = useContext(CVDataContext);
  if (!ctx) throw new Error("useCVData must be used within CVDataProvider");
  return ctx;
}
